import { FC, useMemo } from 'react'
import * as omit from 'lodash/omit'
import moment from 'moment'
import cn from 'classnames'
import styles from 'components/features/pfm/SubscriptionSettingsModule/SubscriptionSettingsModule.styl'
import { useSimpleGuard, isFetchableLoading } from 'helpers/loadableHelpers'
import { subscriptionGuard } from 'guards/subscriptionGuards'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { subscriptionCanBeCancelledSelector, subscriptionInfoSelector } from 'selectors/subscriptionSelectors'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { SubscriptionGeneralSection } from 'components/features/pfm/SubscriptionGeneralSection/SubscriptionGeneralSection'
import { SubscriptionPaymentSourceSection } from 'components/features/pfm/SubscriptionPaymentSourceSection/SubscriptionPaymentSourceSection'
import { SubscriptionAmountSection } from 'components/features/pfm/SubscriptionAmountSection/SubscriptionAmountSection'
import { Subscription } from 'interfaces/subscription'
import { cancelSubscriptionConfirmActionCreator } from 'actions/subscriptionActions'
import { toggleSettingsModalActionCreator } from 'actions/settingsActions'
import { EmailLink } from 'components/features/pfm/EmailLink/EmailLink'
import { SUPPORT_EMAIL } from 'constants/email'
import { ConfirmModalLayout } from 'components/features/pfm/ConfirmModalLayout/ConfirmModalLayout'
import Button from 'components/features/pfm/Button/Button'
import SvgIcon from 'components/atoms/SvgIcon'
import CrossIcon from 'icons/cross.svg'
import { currentCardSelector } from 'components/features/creditCard/creditCardStore/selectors'


type SubscriptionSettingsModuleProps = {

}

export const SubscriptionSettingsModule: FC<SubscriptionSettingsModuleProps> = (props) => {
  const {} = props

  useSimpleGuard(subscriptionGuard, {}, 'SubscriptionSettingsModule')

  const confirmModalShown = useStoreSelector((state) => {
    const { modal } = state.app.settings
    return modal && modal.type === 'cancel-subscription'
  })


  const dispatch = useStoreDispatch()
  const isSubscriptionCanBeCancelled = useStoreSelector(subscriptionCanBeCancelledSelector)
  const fetcheble = useStoreSelector(subscriptionInfoSelector)
  const confirmModalChild = useMemo(() => {
    if (!confirmModalShown) {
      return null
    }

    const handleConfirm = () => dispatch(cancelSubscriptionConfirmActionCreator.success({}, {}))
    const handleCancel = () => dispatch(toggleSettingsModalActionCreator(null))

    const buttons = (
      <div className={ styles.downgradeButtons }>
        <div className={ styles.button }>
          <Button size='tiny' style='hollow' onClick={ handleConfirm }>
            DOWNGRADE
          </Button>
        </div>
        <div className={ styles.button }>
          <EmailLink email={ SUPPORT_EMAIL }>
            <Button size='tiny'>
              CONTACT US
            </Button>
          </EmailLink>
        </div>
      </div>
    )

    const nextBillingDateString: string = fetcheble?.data?.next_billing_date ?? ''
    const nextBillindDateFormatted = moment(nextBillingDateString).format('MMMM Do YYYY')

    return (
      <ConfirmModalLayout
        onCancel={ handleCancel }
        buttons={ buttons }
      >
        <span className={ styles.downgrade }>
          <button className={ styles.downgradeClose } onClick={ handleCancel }>
            <SvgIcon src={ CrossIcon } />
          </button>
          Downgrading will stop future billing. You may continue to enjoy your Premium benefits until { nextBillindDateFormatted }. We’re here to help if you have any issues or questions.
        </span>
      </ConfirmModalLayout>
    )
  }, [fetcheble, dispatch, confirmModalShown])

  const isLoading = isFetchableLoading(fetcheble)
  const loadingChild = useMemo(() => {
    if (!isLoading) {
      return null
    }

    return (
      <div className={ styles.loadingWrapper }>
        <LoadingOverlay />
      </div>
    )
  }, [isLoading])

  const activeCard = useStoreSelector(currentCardSelector)


  const dataChild = useMemo(() => {
    if (!fetcheble.data) {
      return null
    }

    const sub: Subscription = omit(fetcheble.data, [
      'recommended_amount',
    ])

    const subscriptionDetailsChild = !activeCard && (
      <>
        <div className={ cn(styles.section, styles.amountSection) }>
          <SubscriptionAmountSection { ...sub } />
        </div>
        <div className={ cn(styles.section, styles.paymentSourceSection) }>
          <SubscriptionPaymentSourceSection { ...sub } />
        </div>
      </>
    )

    return (
      <>
        <div className={ cn(styles.section, styles.generalSection) }>
          <SubscriptionGeneralSection { ...sub } isSubscriptionCanBeCancelled={ isSubscriptionCanBeCancelled } />
        </div>
        { subscriptionDetailsChild }
      </>
    )

  }, [activeCard, fetcheble.data, isSubscriptionCanBeCancelled])

  const rootClassName = cn(styles.subscriptionSettingsModule, {
    [styles.loadingWithNoData]: fetcheble.isLoading && !fetcheble.data,
  })

  return (
    <div className={ rootClassName }>
      { dataChild }
      { loadingChild }
      { confirmModalChild }
    </div>
  )
}
